/**
 * ProductTagPills.jsx — Iter 172 §3 (Josh-approved, Feb 2026)
 *
 * Renders product-tag pills next to a call log row. Mirrors the canonical
 * slug set defined in `backend/services/call_log_product_tagger.py`.
 *
 * Visual treatment:
 *   • Each slug has a stable accent color so reps recognise pills at a glance.
 *   • The `source` prop ("ai" | "rep" | "ai+rep") drives a tiny ✦ glyph for
 *     AI-detected tags so managers can see which tags the AI guessed vs the
 *     rep confirmed.
 *
 * Used by:
 *   • LeadFocusTabs.jsx → Activity timeline row
 *   • AdminRepActivity.jsx → Activity Detail call list
 *   • PerformanceSnapshotPanel.jsx → Product coverage breakdown (separate
 *     usage — that panel renders its own bar chart, not pills.)
 */
import React from "react";

// Slug → { label, bg, color, border } — keep palette accessible (AA contrast).
export const PRODUCT_TAG_STYLE = {
  ccm:            { label: "CCM",   bg: "#DCFCE7", color: "#15803D", border: "#86EFAC" },
  apcm:           { label: "APCM",  bg: "#CFFAFE", color: "#0E7490", border: "#67E8F9" },
  rpm:            { label: "RPM",   bg: "#DBEAFE", color: "#1D4ED8", border: "#93C5FD" },
  awv:            { label: "AWV",   bg: "#FEF3C7", color: "#92400E", border: "#FCD34D" },
  ehr:            { label: "EHR",   bg: "#EDE9FE", color: "#5B21B6", border: "#C4B5FD" },
  pm:             { label: "PMS",   bg: "#F3E8FF", color: "#6B21A8", border: "#D8B4FE" },
  rcm:            { label: "RCM",   bg: "#FCE7F3", color: "#9D174D", border: "#F9A8D4" },
  revq_ai:        { label: "RevQ AI", bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
  revq_app:       { label: "RevQ App", bg: "#FFE4E6", color: "#9F1239", border: "#FDA4AF" },
  patient_portal: { label: "Patient App", bg: "#E0F2FE", color: "#0369A1", border: "#7DD3FC" },
  telehealth:     { label: "Telehealth", bg: "#ECFCCB", color: "#3F6212", border: "#BEF264" },
};

export const PRODUCT_TAG_OPTIONS = Object.keys(PRODUCT_TAG_STYLE).map((slug) => ({
  slug,
  label: PRODUCT_TAG_STYLE[slug].label,
}));

export function tagLabel(slug) {
  return PRODUCT_TAG_STYLE[slug]?.label || slug;
}

export default function ProductTagPills({ tags = [], source = null, size = "sm" }) {
  if (!Array.isArray(tags) || tags.length === 0) return null;
  const isAI = source === "ai" || source === "ai+rep";
  const fontSize = size === "lg" ? 11 : 9;
  const padding = size === "lg" ? "2px 8px" : "1px 6px";
  return (
    <span
      data-testid="product-tag-pills"
      data-source={source || ""}
      style={{ display: "inline-flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}
    >
      {tags.map((slug) => {
        const s = PRODUCT_TAG_STYLE[slug] || { label: slug, bg: "#F3F4F6", color: "#374151", border: "#E5E7EB" };
        return (
          <span
            key={slug}
            data-testid={`product-tag-${slug}`}
            title={isAI ? "AI-detected" : "Rep-confirmed"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              fontSize,
              fontWeight: 700,
              padding,
              borderRadius: 6,
              background: s.bg,
              color: s.color,
              border: `0.5px solid ${s.border}`,
              letterSpacing: 0.2,
              lineHeight: 1.3,
            }}
          >
            {isAI && <span aria-hidden style={{ fontSize: fontSize - 2 }}>✦</span>}
            {s.label}
          </span>
        );
      })}
    </span>
  );
}
